import type { GameEvent } from '../core/types';
import type { GameSettings } from '../app/settings';

interface Tone {
  frequency: number;
  duration: number;
  gain: number;
  delay?: number;
  type?: OscillatorType;
  destinationFrequency?: number;
}

export class GameAudio {
  private context?: AudioContext;
  private master?: GainNode;
  private settings: Pick<GameSettings, 'masterVolume' | 'muted'>;

  constructor(settings: Pick<GameSettings, 'masterVolume' | 'muted'>) {
    this.settings = settings;
  }

  configure(settings: Pick<GameSettings, 'masterVolume' | 'muted'>): void {
    this.settings = settings;
    if (this.context && this.master) {
      this.master.gain.setTargetAtTime(
        settings.muted ? 0 : settings.masterVolume,
        this.context.currentTime,
        0.02,
      );
    }
  }

  play(events: GameEvent[]): void {
    if (this.settings.muted || this.settings.masterVolume <= 0 || events.length === 0) return;
    // Browser audio is optional; an unavailable device must never reject a game command.
    try {
      this.playFeedback(events);
    } catch {
      return;
    }
  }

  private playFeedback(events: GameEvent[]): void {
    const context = this.ensureContext();
    if (!context) return;
    void context.resume().catch(() => undefined);

    const important = events.find((event) =>
      ['combat_won', 'combat_lost', 'relic_gained', 'overheated'].includes(event.type),
    );
    if (important) {
      this.playEvent(important);
      return;
    }

    const feedback = events.find((event) =>
      ['damage', 'block_gained', 'gear_repaired', 'run_started'].includes(event.type),
    );
    const cardPlayed = events.find((event) => event.type === 'card_played');
    if (feedback) this.playEvent(feedback);
    else if (cardPlayed) this.playEvent(cardPlayed);
  }

  private ensureContext(): AudioContext | undefined {
    if (this.context) return this.context;
    if (typeof window === 'undefined') return undefined;
    const Constructor = window.AudioContext ??
      (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Constructor) return undefined;
    this.context = new Constructor();
    this.master = this.context.createGain();
    this.master.gain.value = this.settings.muted ? 0 : this.settings.masterVolume;
    this.master.connect(this.context.destination);
    return this.context;
  }

  private playEvent(event: GameEvent): void {
    switch (event.type) {
      case 'run_started':
        this.sequence([
          { frequency: 110, destinationFrequency: 165, duration: 0.32, gain: 0.08, type: 'triangle' },
          { frequency: 220, duration: 0.22, gain: 0.04, delay: 0.12, type: 'sine' },
        ]);
        return;
      case 'card_played':
        this.sequence([
          { frequency: 180, destinationFrequency: 90, duration: 0.1, gain: 0.045, type: 'square' },
          { frequency: 520, destinationFrequency: 310, duration: 0.08, gain: 0.025, delay: 0.02 },
        ]);
        return;
      case 'damage':
        this.tone({ frequency: 82, destinationFrequency: 46, duration: 0.16, gain: 0.07, type: 'sawtooth' });
        return;
      case 'block_gained':
        this.sequence([
          { frequency: 240, duration: 0.06, gain: 0.04, type: 'square' },
          { frequency: 360, duration: 0.12, gain: 0.035, delay: 0.04, type: 'triangle' },
        ]);
        return;
      case 'gear_worn':
        this.tone({ frequency: 760, destinationFrequency: 210, duration: 0.18, gain: 0.035, type: 'sawtooth' });
        return;
      case 'gear_repaired':
        this.sequence([
          { frequency: 330, duration: 0.08, gain: 0.04, type: 'triangle' },
          { frequency: 495, duration: 0.12, gain: 0.035, delay: 0.07, type: 'triangle' },
        ]);
        return;
      case 'overheated':
        this.sequence([
          { frequency: 120, destinationFrequency: 55, duration: 0.35, gain: 0.09, type: 'sawtooth' },
          { frequency: 70, duration: 0.28, gain: 0.07, delay: 0.1, type: 'square' },
        ]);
        return;
      case 'relic_gained':
        this.sequence([
          { frequency: 294, duration: 0.2, gain: 0.045, type: 'sine' },
          { frequency: 440, duration: 0.28, gain: 0.04, delay: 0.12, type: 'sine' },
          { frequency: 587, duration: 0.34, gain: 0.035, delay: 0.24, type: 'sine' },
        ]);
        return;
      case 'combat_won':
        this.sequence([
          { frequency: 147, duration: 0.2, gain: 0.055, type: 'triangle' },
          { frequency: 220, duration: 0.25, gain: 0.05, delay: 0.14, type: 'triangle' },
          { frequency: 294, duration: 0.42, gain: 0.045, delay: 0.28, type: 'triangle' },
        ]);
        return;
      case 'combat_lost':
        this.sequence([
          { frequency: 147, destinationFrequency: 110, duration: 0.4, gain: 0.06, type: 'triangle' },
          { frequency: 98, destinationFrequency: 49, duration: 0.65, gain: 0.055, delay: 0.22, type: 'sawtooth' },
        ]);
        return;
      default:
        return;
    }
  }

  private sequence(tones: Tone[]): void {
    for (const tone of tones) this.tone(tone);
  }

  private tone(specification: Tone): void {
    const context = this.context;
    const master = this.master;
    if (!context || !master) return;
    const start = context.currentTime + (specification.delay ?? 0);
    const end = start + specification.duration;
    const oscillator = context.createOscillator();
    const envelope = context.createGain();
    oscillator.type = specification.type ?? 'sine';
    oscillator.frequency.setValueAtTime(specification.frequency, start);
    if (specification.destinationFrequency) {
      oscillator.frequency.exponentialRampToValueAtTime(specification.destinationFrequency, end);
    }
    envelope.gain.setValueAtTime(0.0001, start);
    envelope.gain.exponentialRampToValueAtTime(specification.gain, start + 0.012);
    envelope.gain.exponentialRampToValueAtTime(0.0001, end);
    oscillator.connect(envelope);
    envelope.connect(master);
    oscillator.onended = () => {
      oscillator.disconnect();
      envelope.disconnect();
    };
    oscillator.start(start);
    oscillator.stop(end + 0.02);
  }
}
